import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useServerFn } from "@tanstack/react-start"
import { Loader2 } from "lucide-react"
import { previewOeasShadow, getOeasLiveStatus } from "@/lib/oeas-shadow.functions"
import { Button } from "@/components/ui/button"

export const Route = createFileRoute("/_authenticated/admin/oeas-shadow")({
  head: () => ({ meta: [{ title: "OEAS-01 Model Comparison | XAU Pro Academy Admin" }, { name: "description", content: "Private OEAS control and adaptive ATR research comparison." }, { property: "og:title", content: "OEAS-01 Model Comparison | XAU Pro Academy" }, { property: "og:description", content: "Private administrative research and forward observation." }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex" }] }),
  component: OeasLabPage,
})

type Result = Awaited<ReturnType<typeof previewOeasShadow>>

const fmt = (s: number) => new Date(s * 1000).toISOString().slice(0, 16).replace("T", " ")
const WINDOWS: { d: number | "today"; label: string }[] = [
  { d: "today", label: "Today" },
  { d: 7, label: "7D" },
  { d: 14, label: "14D" },
  { d: 30, label: "30D" },
  { d: 90, label: "90D" },
  { d: 180, label: "6M" },
  { d: 365, label: "12M" },
  { d: 1100, label: "3Y" },
]

function EquityCurve({ points }: { points: { t: number; r: number }[] }) {
  if (points.length < 2) return <p className="text-xs text-muted-foreground">Not enough trades for a curve.</p>
  const w = 300
  const h = 80
  const vals = [0, ...points.map((p) => p.r)]
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const span = max - min || 1
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"}${((i / (vals.length - 1)) * w).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ")
  const zeroY = h - ((0 - min) / span) * h
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-20 w-full" role="img" aria-label="Cumulative R equity curve">
      <line x1={0} x2={w} y1={zeroY} y2={zeroY} className="stroke-border" strokeDasharray="3 3" />
      <path d={path} fill="none" className="stroke-gold" strokeWidth={1.5} />
    </svg>
  )
}


type LiveStatus = Awaited<ReturnType<typeof getOeasLiveStatus>>

function LiveShadowPanel() {
  const load = useServerFn(getOeasLiveStatus)
  const [st, setSt] = useState<LiveStatus | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    const tick = () => load().then((r) => alive && setSt(r)).catch((e) => alive && setErr(e instanceof Error ? e.message : "Load failed"))
    tick()
    const id = setInterval(tick, 60_000)
    return () => { alive = false; clearInterval(id) }
  }, [load])
  const live = st?.live
  const o = live?.open
  const candidate = st?.candidate
  return (
    <div className="rounded-lg border border-gold/40 bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Live Shadow Run · High Conviction+</p>
        <span className={`rounded px-2 py-0.5 font-mono text-[10px] uppercase ${st?.armedFromS ? "bg-emerald/15 text-emerald" : "bg-muted text-muted-foreground"}`}>{st ? (st.armedFromS ? "Armed" : "Disarmed") : "Loading"}</span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">Admin Telegram only · no member delivery</span>
      </div>
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
      {live && (
        <div className="mt-3 space-y-3 font-mono text-xs">
          <p className="text-muted-foreground">Since {fmt(live.fromS)} UTC · last bar {fmt(live.lastBarS)} · last snapshot {st?.lastSnapshotAt ? fmt(Math.floor(new Date(st.lastSnapshotAt).getTime() / 1000)) : "none yet"}</p>
          {o ? (
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border/60 p-3 sm:grid-cols-6">
              <span className="text-emerald">OPEN {o.side}</span><span>Entry {o.entry.toFixed(2)}</span><span>Stop {o.stop.toFixed(2)}</span>
              <span>TP1 {o.tp1.toFixed(2)}</span><span>TP2 {o.tp2.toFixed(2)}</span><span>{o.tp1Hit ? "TP1 hit · BE" : fmt(o.entryTime)}</span>
            </div>
          ) : <p className="text-muted-foreground">No open position. Awaiting NR4 qualification and breakout.</p>}
          <p>Closed {live.summary.trades} · Net {live.summary.netR.toFixed(2)}R · Win rate {live.summary.winRate === null ? "n/a" : `${(live.summary.winRate * 100).toFixed(1)}%`}</p>
        </div>
      )}
      {candidate && <div className="mt-4 border-t border-border/60 pt-3 font-mono text-xs">
        <p className="text-gold">ATR Band Clamp · forward comparison only</p>
        <p className="mt-2 text-muted-foreground">Same observation start · four-week review · no candidate snapshots or alerts</p>
        <p className="mt-2">Closed {candidate.summary.trades} · Net {candidate.summary.netR.toFixed(2)}R · Win rate {candidate.summary.winRate === null ? "n/a" : `${(candidate.summary.winRate * 100).toFixed(1)}%`} · Max DD {candidate.summary.maxDrawdownR.toFixed(2)}R</p>
        {candidate.open ? <p className="mt-2 text-emerald">OPEN LONG · Entry {candidate.open.entry.toFixed(2)} · Stop {candidate.open.stop.toFixed(2)} · TP1 {candidate.open.tp1.toFixed(2)} · TP2 {candidate.open.tp2.toFixed(2)}</p> : <p className="mt-2 text-muted-foreground">No open candidate position.</p>}
      </div>}
    </div>
  )
}

function OeasLabPage() {
  const run = useServerFn(previewOeasShadow)
  const [days, setDays] = useState<number | "today">(1100)
  const [res, setRes] = useState<Result | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState(0)

  async function go() {
    setBusy(true)
    setErr(null)
    try {
      const fromS = days === "today" ? Math.floor(Date.now() / 86400000) * 86400 : Math.floor(Date.now() / 1000) - days * 86400
      setRes(await run({ data: { fromS } }))
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Run failed")
    } finally {
      setBusy(false)
    }
  }

  const sel = res?.variants[tab]

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">OEAS-01 · Expansion Lab</p>
        <h1 className="font-display text-2xl font-bold">Volatility compression breakout: model comparison</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Read-only research. Trading days roll at 22:00 UTC and synthetic weekend bars are excluded. High Conviction+ remains the armed control. ATR Band Clamp changes only its compression cap to max(55, min(80, 0.75 × D1 ATR14)), with Thursday still capped at 30. ATR14 is the mean of 14 completed daily true ranges, including prior-close gaps. Stop 14 and existing targets, weekdays and time shields are unchanged. No trend filter added. The new formula is unvalidated, and the cap alone does not prevent bullish traps.
        </p>
      </div>

      <LiveShadowPanel />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border/60 bg-card p-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Window</span>
        {WINDOWS.map(({ d, label }) => (
          <Button variant="ghost" size="sm"
            key={label}
            onClick={() => setDays(d)}
            className={`rounded-md px-3 py-1 text-xs ${days === d ? "bg-gold-muted text-gold" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </Button>
        ))}
        <Button variant="gold" size="sm" onClick={go} disabled={busy} className="ml-auto">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />} Run preview
        </Button>
      </div>

      {err && <p className="text-sm text-destructive">{err}</p>}

      {res && (
        <>
          <p className="font-mono text-xs text-muted-foreground">
            {fmt(res.fromS)} to {fmt(res.lastBarS)} UTC · trading days {res.tradingDays}
          </p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {res.variants.map(({ summary: s, equity }, i) => {
              const netCls = s.netR > 0 ? "text-emerald" : s.netR < 0 ? "text-destructive" : ""
              return (
                <Button variant="ghost"
                  key={s.id}
                  onClick={() => setTab(i)}
                  className={`h-auto min-w-0 flex-col items-stretch whitespace-normal rounded-lg border bg-card p-4 text-left ${tab === i ? "border-gold" : "border-border/60"}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gold">{s.id.replace("_", " ")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs">
                    <dt className="text-muted-foreground">Trades</dt><dd className="text-right">{s.trades}</dd>
                    <dt className="text-muted-foreground">Win rate</dt><dd className="text-right">{s.winRate === null ? "n/a" : `${(s.winRate * 100).toFixed(1)}%`}</dd>
                    <dt className="text-muted-foreground">Net R</dt><dd className={`text-right font-semibold ${netCls}`}>{s.netR.toFixed(2)}R</dd>
                    <dt className="text-muted-foreground">Profit factor</dt><dd className="text-right">{s.profitFactor === null ? "n/a" : s.profitFactor.toFixed(2)}</dd>
                    <dt className="text-muted-foreground">Max drawdown</dt><dd className="text-right">{s.maxDrawdownR.toFixed(2)}R</dd>
                    <dt className="text-muted-foreground">Max losing run</dt><dd className="text-right">{s.maxLosingStreak}</dd>
                  </dl>
                  <div className="mt-3"><EquityCurve points={equity} /></div>
                </Button>
              )
            })}
          </div>

          {sel && (
            <div className="rounded-lg border border-border/60 bg-card p-4">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Trades · {sel.summary.id.replace("_", " ")}</p>
              {sel.trades.length === 0 ? (
                <p className="text-sm text-muted-foreground">No trades in this window.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-xs">
                    <thead className="text-muted-foreground">
                      <tr className="border-b border-border/60 text-left">
                        <th className="py-1.5 pr-3">Entry (UTC)</th><th className="pr-3">Side</th><th className="pr-3 text-right">D-1 range</th>
                        <th className="pr-3 text-right">Entry</th><th className="pr-3 text-right">Stop</th><th className="pr-3 text-right">TP1</th>
                        <th className="pr-3 text-right">TP2</th><th className="pr-3 text-right">ATR14</th><th className="pr-3 text-right">Cap</th><th className="pr-3">Outcome</th><th className="text-right">R</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...sel.trades].reverse().map((t) => (
                        <tr key={`${t.entryTime}-${t.side}`} className="border-b border-border/30">
                          <td className="py-1.5 pr-3">{fmt(t.entryTime)}</td>
                          <td className={`pr-3 ${t.side === "LONG" ? "text-emerald" : "text-destructive"}`}>{t.side}</td>
                          <td className="pr-3 text-right">{t.prevRange.toFixed(2)}</td>
                          <td className="pr-3 text-right">{t.entry.toFixed(2)}</td>
                          <td className="pr-3 text-right">{t.stop.toFixed(2)}</td>
                          <td className="pr-3 text-right">{t.tp1.toFixed(2)}</td>
                          <td className="pr-3 text-right">{t.tp2.toFixed(2)}</td>
                          <td className="pr-3 text-right">{t.d1Atr14?.toFixed(2) ?? "n/a"}</td>
                          <td className="pr-3 text-right">{t.compressionCapUsd?.toFixed(2) ?? "n/a"}</td>
                          <td className="pr-3">{t.outcome.replaceAll("_", " ")}</td>
                          <td className={`text-right ${t.r > 0 ? "text-emerald" : t.r < 0 ? "text-destructive" : ""}`}>{t.r.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
